import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  RouteEvent,
  RouteEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/transportation/v1/route_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { RouteCreatedDetail } from './RouteCreatedDetail';
import { RouteDeletedDetail } from './RouteDeletedDetail';
import { RouteUpdatedDetail } from './RouteUpdatedDetail';

type RouteEventDetailProps = {
  payload: string;
};

export const RouteEventDetail = ({ payload }: RouteEventDetailProps) => {
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): RouteEvent | null => {
    try {
      return fromJson(RouteEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title="Raw Payload (Parse Error)" />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <RouteCreatedDetail event={inner.value} />;
      case 'updated':
        return <RouteUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <RouteDeletedDetail event={inner.value} />;
      case 'started':
      case 'completed':
      case 'cancelled':
      case 'failed':
      case 'assigned':
      case 'unassigned':
      case 'taskAdded':
      case 'taskRemoved':
        return <JsonViewer data={payload} title={`Route ${inner.case}`} />;
      case undefined:
        return <JsonViewer data={payload} title="Unknown Event Type" />;
    }
  };

  const getEventTitle = (): string => {
    switch (inner.case) {
      case 'created':
        return 'Route Created';
      case 'updated':
        return 'Route Updated';
      case 'deleted':
        return 'Route Deleted';
      case 'started':
        return 'Route Started';
      case 'completed':
        return 'Route Completed';
      case 'cancelled':
        return 'Route Cancelled';
      case 'failed':
        return 'Route Failed';
      case 'assigned':
        return 'Route Assigned';
      case 'unassigned':
        return 'Route Unassigned';
      case 'taskAdded':
        return 'Task Added to Route';
      case 'taskRemoved':
        return 'Task Removed from Route';
      case undefined:
      default:
        return 'Unknown Event';
    }
  };

  return (
    <Col className="@RouteEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
